import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Input,
  Label,
  Textarea
} from '@invent/wl-ui-kit-next/ui';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import * as React from 'react';

import { StatusChip, type ChipTone } from '@/design';
import { formatDateTime } from '@/domains/survey';
import type { IContentImageRef } from './api/media-contracts';
import type { ContentAction, ContentStatus } from './contracts';
import { ImagePicker, useLibrary } from './library';
import { seoGaps } from './store';

/**
 * The parts the website-content screens share (§7.11).
 *
 * **The status strip is not decoration.** Two of these screens still render invented data over
 * no endpoint, and this repo's rule for that is explicit: a screen whose data is invented says so
 * on itself (CLAUDE.md, "Rules of the code"). A fixture-fed screen is otherwise
 * indistinguishable from a database-fed one, and the whole risk of a mock-up is that the gap stops
 * being visible exactly while the layout is being judged and signed off.
 *
 * The news, team and media screens no longer draw it, and that is the point of the strip working the way
 * it does: it is a slot every screen passes, so removing it is a deliberate edit on the screen
 * whose data became real rather than something that quietly stops being true.
 *
 * The strip also carries **what the product would need for the screen to be real**, because that
 * is the question it will be asked in review and the answer differs per screen: events need
 * Cvent credentials, the site pages need tables of their own.
 */

/** What is missing before a screen could be real. One sentence, shown on the screen itself. */
export type Blocker = 'content-store' | 'permissions' | 'pipeline';

const BLOCKER_TEXT: Record<Blocker, string> = {
  'content-store':
    'tables and endpoints of its own — the website store holds the posts, the team page ' +
    'and the media library, and nothing else yet',
  permissions:
    'a permission code for this kind of content — posts have `content.author` and ' +
    '`content.publish`, and a code is a release rather than a row',
  pipeline: 'the site repository and its deployment pipeline (WEB-01), which do not exist yet'
};

export function ContentMockStrip({ needs }: { needs: Blocker[] }) {
  return (
    <div className="flex flex-wrap items-start gap-x-3 gap-y-1.5 border-b border-dashed border-[var(--veritant-yellow-edge)] bg-[color-mix(in_srgb,var(--color-yellow)_18%,white)] px-6 py-2">
      <StatusChip tone="mock">Mock screen</StatusChip>
      <p className="min-w-0 flex-1 text-xs leading-relaxed text-[var(--color-midnight-blue)]">
        Invented content over no endpoint — nothing of it is saved and a reload puts it back. To
        be real it needs{' '}
        {needs.map((need) => BLOCKER_TEXT[need]).join('; ')}.
      </p>
    </div>
  );
}

/** A panel's provenance, for the one or two panels on a screen that differ from the rest. */
export function SourcePill({ children }: { children: React.ReactNode }) {
  return (
    <StatusChip tone="mock" className="text-[0.6875rem]">
      {children}
    </StatusChip>
  );
}

const STATUS_TONE: Record<ContentStatus, ChipTone> = {
  draft: 'info',
  published: 'success',
  archived: 'neutral'
};

const STATUS_WORD: Record<ContentStatus, string> = {
  draft: 'Draft',
  published: 'Published',
  archived: 'Archived'
};

export function StatusPill({ status }: { status: ContentStatus }) {
  return (
    <StatusChip tone={STATUS_TONE[status]} dot>
      {STATUS_WORD[status]}
    </StatusChip>
  );
}

const ACTION_WORD: Record<ContentAction, string> = {
  created: 'created',
  saved: 'saved',
  submitted: 'marked finished',
  published: 'published',
  unpublished: 'took off the site',
  reverted: 'restored an earlier version',
  restored: 'brought back as a draft'
};

export function actionWord(action: ContentAction): string {
  return ACTION_WORD[action];
}

/**
 * A person, as this app can name one.
 *
 * survey-api's accounts carry no display name — only an address — so an address is what every
 * screen over the content store prints. Said once here rather than at each of the six sites, so
 * the day accounts grow a name there is one place to change.
 */
export function personName(account: Nullable<{ email: string }>): string {
  return account?.email ?? 'the system';
}

/** A title to a URL segment — lowercase, words joined by hyphens, nothing else kept. The same
 *  rule survey-api applies when a client sends none, so what is typed is what is saved. */
export function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

/**
 * The address a new draft is created at: its placeholder title plus a token, so it cannot collide.
 * The title is the same every time, and the address derived from it would be taken by the first
 * untitled record — for good, once that one was published. It follows the title from the first
 * edit, so nobody ever sees it on the site.
 */
export function draftSlug(placeholder: string): string {
  return `${slugify(placeholder)}-${Date.now().toString(36)}`;
}

/* ── media ─────────────────────────────────────────────────────────────────────────────── */

/** How large a file is, in the unit a person would say it in. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/* ── metadata (WEB-15) ─────────────────────────────────────────────────────────────────── */

/**
 * Per-page metadata, with the link preview it decides (WEB-15).
 *
 * The preview is the point of the panel. AC2 asks that a shared link render with the configured
 * title, description and image, and the failure it guards against is silent: nobody sees a bad
 * preview until the link is already in a channel. Drawing the card beside the fields makes the
 * empty state visible while it can still be fixed, which is why the gaps are named out loud
 * rather than left to the editor to notice.
 */
export function SeoFields<T extends { title: string; description: string }>({
  seo,
  onChange,
  pageTitle,
  socialImageId,
  socialImage,
  onPickSocialImage
}: {
  seo: T;
  onChange: (next: T) => void;
  /** What the template would fall back to, shown in the preview when the title is blank. */
  pageTitle: string;
  /**
   * The sharing image (WEB-15 AC2), as a library id. A screen that passes neither draws no
   * control and reports no gap — which is what a screen with nowhere to save one must do.
   */
  socialImageId?: Nullable<string>;
  /** The sharing image as the record answered it — see `ImagePicker`'s `current` for why the
   *  library alone is not enough to know what is already placed. */
  socialImage?: Nullable<IContentImageRef>;
  onPickSocialImage?: (id: Nullable<string>) => void;
}) {
  const { images } = useLibrary();
  const image =
    images.find((row) => row.imageId === socialImageId)
    ?? (socialImageId && socialImage?.imageId === socialImageId ? socialImage : null);
  const gaps = seoGaps({ ...seo, socialImageId: onPickSocialImage ? socialImageId ?? null : undefined });

  return (
    <div className="grid gap-4 md:grid-cols-[1fr_18rem]">
      <div className="grid gap-3">
        <div className="grid gap-1.5">
          <Label htmlFor="seo-title">Page title</Label>
          <Input
            id="seo-title"
            value={seo.title}
            placeholder={pageTitle}
            onChange={(e) => onChange({ ...seo, title: e.target.value })}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="seo-description">Description</Label>
          <Textarea
            id="seo-description"
            rows={3}
            value={seo.description}
            onChange={(e) => onChange({ ...seo, description: e.target.value })}
          />
          <p className="text-xs text-muted-foreground">
            {seo.description.length} characters — search results cut around 155.
          </p>
        </div>
        {onPickSocialImage && (
          <ImagePicker
            label="Sharing image"
            value={socialImageId ?? null}
            current={socialImage}
            onPick={onPickSocialImage}
          />
        )}
        {gaps.length > 0 && (
          <p className="text-xs text-[var(--color-deep-magenta)]">
            Not set: {gaps.join(', ')}. A shared link falls back to whatever the template carries.
          </p>
        )}
      </div>

      <figure className="grid gap-2 self-start">
        <figcaption className="text-xs font-medium text-muted-foreground">
          How a shared link looks
        </figcaption>
        <div className="overflow-hidden rounded-md border border-border">
          {/* The real picture, at the shape a card draws it in — the point of the panel is that
              an empty or wrong preview is visible here rather than in a channel (AC2). */}
          {image ? (
            <img
              src={image.url}
              alt={image.alt}
              loading="lazy"
              className="h-28 w-full object-cover"
            />
          ) : (
            <div className="h-28 w-full bg-[var(--color-slight-blue)]" />
          )}
          <div className="grid gap-1 bg-white px-3 py-2">
            <span className="text-[0.625rem] tracking-wide text-muted-foreground uppercase">
              veritant.com
            </span>
            <span className="line-clamp-2 text-xs font-medium text-[var(--color-royal-blue)]">
              {seo.title.trim() || pageTitle}
            </span>
            <span className="line-clamp-2 text-[0.6875rem] text-muted-foreground">
              {seo.description.trim() || 'No description set.'}
            </span>
          </div>
        </div>
      </figure>
    </div>
  );
}

/* ── concurrency and history (WEB-11, WEB-14) ──────────────────────────────────────────── */

/**
 * WEB-11 AC1 — the save that was refused because the record moved underneath it.
 *
 * It offers no merge and no "save anyway". Both would need a decision this screen cannot make for
 * the editor, and "save anyway" is the silent overwrite the requirement exists to prevent. Reload
 * and reapply is the honest pair of options, and the one survey-api's own 409 leaves a caller.
 */
export function ConflictNotice({
  by,
  onReload
}: {
  by: string;
  onReload: () => void;
}) {
  return (
    <Alert variant="destructive">
      <AlertTriangle className="h-4 w-4" aria-hidden />
      <AlertTitle>Not saved — {by} changed this while you were editing</AlertTitle>
      <AlertDescription className="grid gap-2">
        <p>
          Nothing of yours was written and nothing of theirs was overwritten. Load their version
          and put your change back on top of it.
        </p>
        <div>
          <Button type="button" size="sm" variant="outline" onClick={onReload}>
            Load their version
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}

/**
 * The version history, and the revert (WEB-14).
 *
 * A revert is offered on every entry but the current one, and it appends rather than rewinds —
 * `store.tsx` says why. The list is newest first because the question asked of it is almost
 * always "what just happened", not "how did this start".
 */
export interface IHistoryEntry {
  versionNo: number;
  at: string;
  /** Who did it, already said in words: a mock record carries a name and a post an email
   *  address, and the panel is not the place to learn the difference. */
  by: string;
  action: ContentAction;
  note?: Nullable<string>;
}

export function HistoryPanel({
  history,
  onRevert
}: {
  history: IHistoryEntry[];
  onRevert?: (versionNo: number) => void;
}) {
  const newest = history.at(-1)?.versionNo;
  return (
    <ol className="grid gap-0">
      {[...history].reverse().map((version) => (
        <li
          key={version.versionNo}
          className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 border-b border-border py-2 text-sm last:border-b-0"
        >
          <span className="font-medium text-[var(--color-royal-blue)]">v{version.versionNo}</span>
          <span className="text-muted-foreground">{formatDateTime(version.at)}</span>
          <span>
            {version.by} {actionWord(version.action)}
          </span>
          {version.note && (
            <span className="w-full text-xs text-muted-foreground">{version.note}</span>
          )}
          {onRevert && version.versionNo !== newest && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="ml-auto"
              onClick={() => onRevert(version.versionNo)}
            >
              <RotateCcw className="mr-1 h-3 w-3" aria-hidden />
              Restore
            </Button>
          )}
        </li>
      ))}
    </ol>
  );
}

/** An empty list, said in a sentence rather than drawn as a blank panel. */
export function EmptyNote({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-md border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
      {children}
    </p>
  );
}

/**
 * One labelled control, with the server's refusal under it where there is one.
 *
 * Here rather than in a screen because two editors draw it — the post's and the event's — and a
 * second copy is how the two drift into looking like different forms.
 */
export function Field({
  id,
  label,
  refusal,
  children
}: {
  id: string;
  label: string;
  refusal?: { reason: string };
  children: React.ReactNode;
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {refusal && <FieldRefusal reason={refusal.reason} />}
    </div>
  );
}

/** survey-api's own sentence, shown as it was written: it names the limit, and a paraphrase here
 *  would be a second limit to keep in step with the first. */
export function FieldRefusal({ reason }: { reason: string }) {
  return <p className="text-xs text-[var(--color-deep-magenta)]">This {reason}</p>;
}
